<template>
  <Menu as="div" v-slot="{ close }" class="relative notifications-menu">
    <div class="relative">
      <MenuButton
        class="notifications-btn w-11 h-11 flex items-center justify-center rounded-xl text-gray-600 hover:text-gray-700 dark:text-white/90 dark:hover:text-white transition-all duration-300 shadow-md hover:shadow-lg backdrop-blur-sm hover:scale-105 bg-white dark:bg-gradient-to-br dark:from-[#007aff] dark:to-[#5856d6] border border-black/10 dark:border-white/20"
      >
      <i class="ri-notification-3-line text-lg"></i>
      </MenuButton>
      <span
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 min-w-[18px] h-5 bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse border-2 border-white/20"
      >
        {{ unreadCount > 99 ? "99+" : unreadCount }}
      </span>
    </div>

    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95 translate-y-2"
      enter-to-class="transform opacity-100 scale-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100 scale-100 translate-y-0"
      leave-to-class="transform opacity-0 scale-95 translate-y-2"
    >
      <MenuItems
        class="absolute mt-3 w-[320px] md:w-[380px] z-[10000] top-full ltr:-right-[140px] ltr:md:right-0 rtl:-left-[140px] rtl:md:left-0 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden focus:outline-none"
      >
        <!-- Header -->
        <div
          class="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900 dark:to-purple-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg"
              >
                <i class="ri-notification-3-fill text-lg"></i>
              </div>
              <div>
                <h3 class="font-bold text-gray-900 dark:text-white text-lg">
                  {{ directT("notifications.title") }}
                </h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ directT("notifications.unreadCount", { count: unreadCount }) }}
                </p>
              </div>
            </div>
            <button
              @click="
                markAllAsRead();
                close();
              "
              class="px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
              style="
                background: linear-gradient(135deg, #8b5cf6 0%, #2563eb 100%);
                color: #ffffff;
              "
              @mouseenter="
                $event.target.style.background =
                  'linear-gradient(135deg, #7c3aed 0%, #1d4ed8 100%)'
              "
              @mouseleave="
                $event.target.style.background =
                  'linear-gradient(135deg, #8b5cf6 0%, #2563eb 100%)'
              "
            >
              {{ directT("notifications.markAllAsRead") }}
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="max-h-[400px] overflow-y-auto custom-scrollbar">
          <!-- Loading State -->
          <div v-if="loading" class="flex flex-col items-center justify-center py-12">
            <div class="relative">
              <div
                class="w-12 h-12 border-4 border-purple-200 dark:border-purple-800 rounded-full animate-spin"
              ></div>
              <div
                class="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-purple-600 rounded-full animate-spin"
              ></div>
            </div>
            <p class="text-gray-500 dark:text-gray-400 mt-3 text-sm">{{ directT("notifications.loadingNotifications") }}</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="flex flex-col items-center justify-center py-12">
            <div
              class="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4"
            >
              <i class="ri-error-warning-line text-2xl text-red-500"></i>
            </div>
            <p class="text-red-500 text-center text-sm">{{ error }}</p>
          </div>

          <!-- Empty State -->
          <div
            v-else-if="notifications.length === 0"
            class="flex flex-col items-center justify-center py-12"
          >
            <div
              class="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-2xl flex items-center justify-center mb-4 shadow-lg"
            >
              <i class="ri-notification-off-line text-3xl text-gray-400"></i>
            </div>
            <h4 class="font-semibold text-gray-900 dark:text-white mb-2">
              {{ directT("notifications.noNewNotifications") }}
            </h4>
            <p class="text-gray-500 dark:text-gray-400 text-sm text-center">
              {{ directT("notifications.allNotificationsRead") }}
            </p>
          </div>

          <!-- Notifications List -->
          <div v-else class="p-2">
            <div
              v-for="notification in notifications"
              :key="notification.id"
              class="group relative bg-white dark:bg-gray-700 rounded-xl p-4 mb-3 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] cursor-pointer"
              @click="
                markAsRead(notification);
                close();
              "
            >
              <!-- Notification Icon -->
              <div class="flex items-start gap-4">
                <div class="relative flex-shrink-0">
                  <div
                    class="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                    :class="getNotificationStyle(notification.type)"
                  >
                    <i
                      :class="getNotificationIcon(notification.type)"
                      class="text-xl text-white"
                    ></i>
                  </div>
                  <!-- Unread Indicator -->
                  <div
                    v-if="notification.status === 'unread'"
                    class="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-red-500 to-pink-500 rounded-full border-2 border-white dark:border-gray-800 animate-pulse"
                  ></div>
                </div>

                <!-- Content -->
                <div class="flex-1 min-w-0">
                  <p
                    class="text-gray-900 dark:text-white font-medium leading-relaxed mb-2"
                  >
                    {{ notification.content }}
                  </p>
                  <div
                    class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
                  >
                    <i class="ri-time-line text-xs"></i>
                    <span>{{ formatDate(notification.timestamp) }}</span>
                    <span
                      v-if="notification.status === 'unread'"
                      class="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-xs font-medium"
                    >
                      {{ directT("notifications.newNotification") }}
                    </span>
                  </div>
                </div>

                <!-- Action Button -->
                <div
                  class="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                >
                  <button
                    class="w-8 h-8 bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <i class="ri-close-line text-sm text-gray-500 hover:text-red-500"></i>
                  </button>
                </div>
              </div>

              <!-- Hover Effect -->
              <div
                class="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              ></div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700"
        >
          <RouterLink
            to="/notifications"
            class="flex items-center justify-center gap-2 w-full py-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium transition-colors duration-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg"
            @click="close()"
          >
            <i class="ri-eye-line text-lg"></i>
            {{ directT("navbar.seeAllNotifications") }}
          </RouterLink>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted } from "vue";
import { Menu, MenuButton, MenuItems } from "@headlessui/vue";
import { supabase } from "@/utils/supabase";
import { useI18n } from "vue-i18n";

// Direct import of translation files for manual translation lookup
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Create type for translation object structure
type TranslationObject = Record<string, any>;

interface Notification {
  id: string;
  userid: string;
  timestamp: string;
  type: string;
  content: string;
  status: string;
}

export default defineComponent({
  name: "NotificationsLists",
  components: {
    Menu,
    MenuButton,
    MenuItems,
  },
  setup() {
    const { t, locale } = useI18n();
    const notifications = ref<Notification[]>([]);
    const loading = ref(false);
    const error = ref("");
    let notificationsSubscription: any = null;

    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations,
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string, params?: Record<string, any>): string => {
      const parts = key.split(".");
      let result: TranslationObject =
        translationFiles[locale.value as keyof typeof translationFiles];

      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[
              fallbackPart as keyof typeof enResult
            ] as TranslationObject;
          }
          result = enResult;
          break;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      
      // Ensure we always return a string
      let finalResult = typeof result === 'string' ? result : String(result);
      
      // Handle interpolation with parameters
      if (params) {
        finalResult = Object.keys(params).reduce((str, param) => {
          return str.replace(new RegExp(`\\{${param}\\}`, 'g'), String(params[param]));
        }, finalResult);
      }
      
      return finalResult;
    };

    // Format date with internationalization support
    const formatDate = (timestamp: string) => {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMinutes < 1) {
        return directT("notifications.justNow");
      } else if (diffMinutes < 60) {
        return directT("notifications.minutesAgo", { minutes: diffMinutes });
      } else if (diffHours < 24) {
        return directT("notifications.hoursAgo", { hours: diffHours });
      } else if (diffDays < 7) {
        return directT("notifications.daysAgo", { days: diffDays });
      } else {
        // For older dates, show the actual date
        return date.toLocaleDateString(locale.value === 'he' ? 'he-IL' : locale.value === 'ar' ? 'ar-SA' : 'en-US');
      }
    };



    const unreadCount = computed(() => {
      return notifications.value.filter((n) => n.status === "unread").length;
    });

    const fetchNotifications = async () => {
      try {
        loading.value = true;
        error.value = "";

        const { data: userData, error: userError } = await supabase.auth.getUser();

        if (userError) {
          throw new Error(userError.message);
        }

        if (!userData || !userData.user) {
          throw new Error("No authenticated user found");
        }

        const { data, error: notifError } = await supabase
          .from("notifications")
          .select("*")
          .eq("userid", userData.user.id)
          .eq("status", "unread")
          .order("timestamp", { ascending: false })
          .limit(5);

        if (notifError) {
          throw new Error(notifError.message);
        }

        notifications.value = (data as any[]) || [];
      } catch (err) {
        console.error("Error fetching notifications:", err);
        error.value = err instanceof Error ? err.message : directT("notifications.errorLoadingNotifications");
      } finally {
        loading.value = false;
      }
    };

    const markAsRead = async (notification: Notification) => {
      try {
        const { error } = await supabase
          .from("notifications")
          .update({ status: "read" })
          .eq("id", notification.id);

        if (error) {
          throw new Error(error.message);
        }

        // Update local state
        const index = notifications.value.findIndex((n) => n.id === notification.id);
        if (index !== -1) {
          notifications.value.splice(index, 1);
        }
      } catch (err) {
        console.error("Error marking notification as read:", err);
      }
    };

    const markAllAsRead = async () => {
      try {
        if (notifications.value.length === 0) {
          console.log("No notifications to mark as read");
          return;
        }

        console.log("Marking all notifications as read");

        const { data: userData } = await supabase.auth.getUser();
        if (!userData || !userData.user) {
          console.error("No authenticated user found");
          return;
        }

        loading.value = true;

        const { data, error } = await supabase
          .from("notifications")
          .update({ status: "read" })
          .eq("userid", userData.user.id)
          .eq("status", "unread")
          .select();

        if (error) {
          console.error("Failed to update notifications:", error);
          throw new Error(error.message);
        }

        console.log(`Successfully marked ${data?.length || 0} notifications as read`);

        // הרענון יקרה אוטומטית דרך Realtime
      } catch (err) {
        console.error("Error marking all notifications as read:", err);
      } finally {
        loading.value = false;
      }
    };

    // הוספת Supabase Realtime הקשבה לשינויים
    const setupRealtimeListener = async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData || !userData.user) return;

        // הקשבה לטבלת notifications
        notificationsSubscription = supabase
          .channel("notifications-changes")
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "notifications",
              filter: `userid=eq.${userData.user.id}`,
            },
            (payload) => {
              console.log("Realtime notification change:", payload);

              // רענון ההתראות כשיש שינוי
              fetchNotifications();
            }
          )
          .subscribe();

        console.log("Realtime subscription setup for notifications");
      } catch (err) {
        console.error("Error setting up realtime listener:", err);
      }
    };



    const getNotificationIcon = (type: string) => {
      switch (type) {
        case "alert":
        case "error":
          return "ri-alarm-warning-line";
        case "information":
        case "info":
          return "ri-information-line";
        case "announcement":
          return "ri-megaphone-line";
        case "success":
          return "ri-check-line";
        case "warning":
          return "ri-error-warning-line";
        case "message":
          return "ri-message-2-line";
        case "task":
          return "ri-task-line";
        case "reminder":
          return "ri-alarm-line";
        case "system":
          return "ri-settings-3-line";
        default:
          return "ri-notification-3-line";
      }
    };

    const getNotificationStyle = (type: string) => {
      switch (type) {
        case "alert":
        case "error":
          return "bg-gradient-to-r from-red-500 to-pink-500";
        case "information":
        case "info":
          return "bg-gradient-to-r from-blue-500 to-cyan-500";
        case "announcement":
          return "bg-gradient-to-r from-purple-500 to-indigo-500";
        case "success":
          return "bg-gradient-to-r from-green-500 to-emerald-500";
        case "warning":
          return "bg-gradient-to-r from-orange-500 to-yellow-500";
        case "message":
          return "bg-gradient-to-r from-purple-500 to-purple-600";
        case "task":
          return "bg-gradient-to-r from-indigo-500 to-indigo-600";
        case "reminder":
          return "bg-gradient-to-r from-pink-500 to-pink-600";
        case "system":
          return "bg-gradient-to-r from-gray-500 to-gray-600";
        default:
          return "bg-gradient-to-r from-gray-500 to-slate-500";
      }
    };

    onMounted(() => {
      fetchNotifications();
      setupRealtimeListener();
    });

    onUnmounted(() => {
      // ניקוי הקשבה בזמן אמת
      if (notificationsSubscription) {
        notificationsSubscription.unsubscribe();
      }
    });

    return {
      notifications,
      loading,
      error,
      unreadCount,
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      formatDate,
      getNotificationIcon,
      getNotificationStyle,
      directT,
    };
  },
});
</script>



<style scoped>
/* Custom Scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(139, 92, 246, 0.3) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(59, 130, 246, 0.3));
  border-radius: 10px;
  border: 1px solid transparent;
  background-clip: content-box;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.5), rgba(59, 130, 246, 0.5));
  background-clip: content-box;
}

/* Notification Item Animations */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.notification-item {
  animation: slideInUp 0.3s ease-out;
}

/* Hover Effects */
.notification-item:hover {
  transform: translateY(-2px);
}

/* Badge Animation */
@keyframes badgePulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.animate-pulse {
  animation: badgePulse 2s infinite;
}

/* Loading Animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Bounce Animation */
@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.group-hover\:animate-bounce:hover {
  animation: bounce 0.6s ease-in-out;
}

/* Dark Mode Enhancements */
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(59, 130, 246, 0.4));
  background-clip: content-box;
}

/* Responsive Design */
@media (max-width: 768px) {
  .notification-item {
    padding: 12px;
  }
}

/* Focus States */
button:focus-visible {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

/* Selection */
::selection {
  background: rgba(139, 92, 246, 0.2);
}

.dark ::selection {
  background: rgba(139, 92, 246, 0.3);
}
</style>
